import { Injectable } from '@angular/core';
import {
  arrayUnion,
  collection,
  collectionData,
  deleteDoc,
  deleteField,
  doc,
  docData,
  DocumentData,
  FieldValue,
  Firestore,
  getDocs,
  orderBy,
  query,
  QuerySnapshot,
  setDoc,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { CustomAttributes } from '@yoimo/interfaces';
import { chunk } from 'lodash';
import { map, Observable, of } from 'rxjs';
import { Team } from '../interfaces/teams';
import { AuthGuard, Role } from './userSession.service';
export enum ChannelInfoTabs {
  STREAMS = 'Streams',
  TEAMS = 'Sub-Channels',
  USERS = 'Users',
  CLIPS = 'Clips',
}

export interface CoverImage {
  url: string;
  historicEvents: boolean;
}

type TeamUpdatePayload = {
  'channelDetails.defaultTags'?: string[] | FieldValue;
  'channelDetails.defaultEventId'?: string | null | FieldValue;
  'channelDetails.defaultAttribute'?: CustomAttributes | FieldValue;
};

export interface FilterMenuTeams {
  showTeamName: boolean;
  showTime: boolean;
  showUser: boolean;
  showCreatedBy: boolean;
  showCreatedName: boolean;
}

export interface FilterInputTeams {
  timeValue: number;
  teamName: string;
  userId: string;
  adminId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  tabId: string;
  tabIds: Record<string, string> = {
    HOME: 'Home',
    STREAMS: 'Streams',
    TEAMS: 'Sub-Channels',
    USERS: 'Users',
    CLIPS: 'Clips',
  };
  filterMenu: FilterMenuTeams = {
    showTime: false,
    showTeamName: false,
    showUser: false,
    showCreatedBy: false,
    showCreatedName: true,
  };
  filterInput: FilterInputTeams = {
    timeValue: 0,
    teamName: '',
    userId: '',
    adminId: '',
  };
  teamsLoader = false;
  channelTabText = '';
  masterChannelTabIds: Record<string, string> = {
    HOME: 'Home',
    VIDEOS: 'Videos',
    PAGES: 'Pages',
    EVENTS: 'Events',
    NAMED_TAGS: 'Named Tags',
    ATTACHED_CHANNELS: 'Attached Channels',
    SETTINGS: 'Settings',
    SUBSCRIPTIONS: 'Subscriptions'
  };
  masterChannelTabId: string;
  userRole: Role;
  roles = Role;
  constructor(
    private readonly firestore: Firestore,
    private readonly userSession: AuthGuard,
    private readonly functions: Functions
  ) {
    this.userRole = this.userSession.userRole;
    this.tabId = this.tabIds.HOME;
    this.setTabId();
    this.masterChannelTabId = this.masterChannelTabIds.HOME;
  }

  setTabId() {
    if (this.userRole === this.roles.SALES) {
      this.tabIds = {
        HOME: 'Home',
        STREAMS: 'Streams',
        CLIPS: 'Clips',
      };
    }
  }

  resetTabIds() {
    if (this.userRole === this.roles.SALES) {this.setTabId();}
    else {
      this.tabIds = {
        HOME: 'Home',
        STREAMS: 'Streams',
        TEAMS: 'Sub-Channels',
        USERS: 'Users',
        CLIPS: 'Clips',
      };
    }
  }

  getTeamsData() {
    const teamsCollection = collection(this.firestore, 'teams');
    return collectionData(teamsCollection);
  }

  getTeamsDataBetweenDates(dateRange: { fromDate: string, toDate: string }) {
    const teamsCollection = collection(this.firestore, 'teams');
    const q = query(
      teamsCollection,
      orderBy('createdTime', 'asc'),
      where('createdTime', '>=', new Date(dateRange.fromDate)),
      where('createdTime', '<=', new Date(dateRange.toDate))
    );

    return collectionData(q, { idField: 'docId' }) as Observable<Team[]>;
  }

  async getTeamsDataForClub(clubId: string) {
    const teamsCollection = collection(this.firestore, 'teams');
    const q = query(teamsCollection, where('clubDocId', '==', clubId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = doc.data() as Team;
      const docId = doc.id;
      return { docId, ...data };
    });
  }

  getTeamsDataByClub(
    clubId: string,
    collectionName?: string,
    isDefaultTeam?: boolean
  ) {
    const teamsCollection = collection(this.firestore, 'teams');

    if (collectionName === 'clubs') {
      const constraints = [where('clubDocId', '==', clubId)];
      if (isDefaultTeam !== undefined) {
        constraints.push(where('isDefaultItem', '==', isDefaultTeam));
      }
      const q = query(teamsCollection, ...constraints);
      return collectionData(q, { idField: 'docId' });
    } else if (collectionName === 'archive') {
      const archiveTeamsCollection = collection(
        this.firestore,
        `archive/${clubId}/teams`
      );
      return collectionData(archiveTeamsCollection, { idField: 'docId' });
    }

    const q = query(teamsCollection, where('clubDocId', '==', clubId));
    return collectionData(q, { idField: 'docId' });
  }

  getArchivedTeamsData(clubId: string) {
    const archiveTeamsCollection = collection(
      this.firestore,
      `archive/${clubId}/teams`
    );
    return collectionData(archiveTeamsCollection, { idField: 'docId' }).pipe(
      map((result) => result.map((record) => ({ isArchived: true, ...record })))
    );
  }

  getTeamById(id: string) {
    const teamCollection = doc(this.firestore, 'teams', id);
    return docData(teamCollection) as Observable<Team>;
  }

  deleteTeam(teamId: string): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', teamId);
    return deleteDoc(teamDoc);
  }

  retrieveTeam(clubId: string, teamId: string): Promise<void> {
    const archivedTeamDoc = doc(
      this.firestore,
      `archive/${clubId}/teams`,
      teamId
    );
    return deleteDoc(archivedTeamDoc);
  }

  uploadCoverImage(
    teamId: string,
    obj: { coverImage: CoverImage }
  ): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', teamId);
    return updateDoc(teamDoc, obj);
  }

  createTeam(teamDocId: string, newTeam: Team): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', teamDocId);
    return setDoc(teamDoc, newTeam);
  }

  editTeamLogo(docId: string, url: string): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, { imageUrl: url });
  }

  updateTeam(
    docId: string,
    defaultTags: string[] | undefined,
    defaultEventId: string | undefined,
    isTagsEnabled: boolean,
    isEventsEnabled: boolean,
    isAttributesEnabled: boolean,
    defaultAttributes?: CustomAttributes | undefined 
  ) {
    const updatePayload: TeamUpdatePayload = {
      'channelDetails.defaultTags': !!defaultTags && isTagsEnabled ? defaultTags : deleteField(),
      'channelDetails.defaultEventId': isEventsEnabled ? defaultEventId || '' : deleteField(),
      'channelDetails.defaultAttribute': isAttributesEnabled ? defaultAttributes : deleteField()
    };
   
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, updatePayload);
  }

  async removeShareAppTags(docId: string, tag: string) {
    const updateTagsFunction = httpsCallable(
      this.functions,
      'teams-updateeventsandtags'
    );
    return (await updateTagsFunction({ availableTags: tag, teamDocId: docId }))
      .data;
  }

  addShareAppTags(
    docId: string,
    tag: string[],
    channelId: string
  ): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, {
      'channelDetails.availableTags': arrayUnion(...tag),
      'channelDetails.channelId': channelId,
    });
  }

  addShareAppEvents(
    docId: string,
    addedEvent: string,
    channelId: string
  ): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, {
      'channelDetails.availableEvents': arrayUnion(addedEvent),
      'channelDetails.channelId': channelId,
    });
  }

  updateShareAppEvents(
    docId: string,
    addEvents = false,
    channelId: string
  ): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, {
      'channelDetails.availableEvents': addEvents ? [] : deleteField(),
      'channelDetails.channelId': channelId,
    });
  }

  updateShareAppTags(
    docId: string,
    addTags = false,
    channelId: string
  ): Promise<void> {
    const teamDoc = doc(this.firestore, 'teams', docId);
    return updateDoc(teamDoc, {
      'channelDetails.availableTags': addTags ? [] : deleteField(),
      'channelDetails.channelId': channelId,
    });
  }

  async removeShareAppEvents(docId: string, eventId: string) {
    const updateEventsFunction = httpsCallable(
      this.functions,
      'teams-updateeventsandtags'
    );
    return (
      await updateEventsFunction({ availableEvents: eventId, teamDocId: docId })
    ).data;
  }

  async getTeamDataByClubIdArray(clubDocIds: string[]) {
    const queryPromises = chunk(clubDocIds, 10).map((slice) => {
      const teamsCollection = collection(this.firestore, 'teams');
      const q = query(teamsCollection, where('clubDocId', 'in', slice));
      return getDocs(q);
    });

    const querySnapshots = await Promise.all(queryPromises);
    return querySnapshots.reduce(
      (acc: DocumentData[], batchResult: QuerySnapshot<DocumentData>) => [...acc, ...batchResult.docs],
      []
    );
  }

  fetchAllNamedTags(channelId: string){
    if (!channelId) {return of([]);}
    const tagsCollection = collection(this.firestore, 'channels', channelId, 'tags');
    return collectionData(tagsCollection)
      .pipe(
        map((tags) =>
          tags
            .map(t => t.name)
        )
      );
  }
}
